import { supabaseDataProvider } from "ra-supabase-core";
import { createClient } from "@supabase/supabase-js";
import {
  withLifecycleCallbacks,
  type DataProvider,
  type GetListParams,
  type Identifier,
  type ResourceCallbacks,
} from "ra-core";
import type {
  ContactNote,
  Deal,
  DealNote,
  RAFile,
  Sale,
  SalesFormData,
  SignUpData,
} from "../../types";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import { ATTACHMENTS_BUCKET } from "../commons/attachments";
import { getIsInitialized, getSale } from "./authProvider";
import { getSupabaseClient, getSupabaseUrl } from "./supabase";
import { isSuperAdminEmail } from "../../admin/superadminConfig";

const getBaseDataProvider = () =>
  supabaseDataProvider({
    instanceUrl: getSupabaseUrl(),
    apiKey: "dotus_crm_client_public",
    supabaseClient: getSupabaseClient(),
    sortOrder: "asc,desc.nullslast" as any,
  });

const processCompanyLogo = async (params: any) => {
  const logo = params.data.logo;

  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

const getTenantActiveCrmId = (): string | null => {
  if (typeof window !== "undefined" && window.localStorage) {
    const raw = window.localStorage.getItem("dotus_active_crm_id");
    if (!raw || raw === "00000000-0000-0000-0000-000000000001") {
      return "fe386f56-b27f-446f-86ec-39a03b64add1"; // Grupo Dotus
    }
    return raw;
  }
  return "fe386f56-b27f-446f-86ec-39a03b64add1";
};

const TENANT_RESOURCES = [
  "companies",
  "contacts",
  "companies_summary",
  "contacts_summary",
  "deals",
  "deal_notes",
  "contact_notes",
  "tasks",
  "tags",
  "pipelines",
  "custom_field_definitions",
  "goals",
];

const recentAutomationTriggers = new Map<string, number>();

export const triggerAutomation = async (
  resource: string,
  evento: "criado" | "atualizado" | "excluido",
  recordId: string | number,
  dados: Record<string, any>,
  dadosAnteriores?: Record<string, any>,
) => {
  const resourceMap: Record<
    string,
    "negocio" | "pessoa" | "organizacao" | "atividade"
  > = {
    deals: "negocio",
    contacts: "pessoa",
    companies: "organizacao",
    tasks: "atividade",
  };

  const item = resourceMap[resource];
  if (!item) return;

  const activeCrmId =
    dados?.crm_id || dadosAnteriores?.crm_id || getTenantActiveCrmId();
  if (!activeCrmId) return;

  // Deduplicação em janela curta (1.5s) para evitar disparos duplicados simultâneos
  const fingerprint = `${activeCrmId}:${item}:${evento}:${recordId}:${dados?.stage ?? ""}:${dados?.status ?? ""}`;
  const now = Date.now();
  const lastFired = recentAutomationTriggers.get(fingerprint);
  if (lastFired && now - lastFired < 1500) {
    return;
  }
  recentAutomationTriggers.set(fingerprint, now);

  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "x-crm-id": activeCrmId,
    };

    try {
      const { data: sessionData } = await getSupabaseClient().auth.getSession();
      if (sessionData?.session?.access_token) {
        headers["Authorization"] = `Bearer ${sessionData.session.access_token}`;
      }
    } catch {
      // prossegue mesmo se a sessão não puder ser lida
    }

    // trigger automation
    fetch("/api/automations?action=fire", {
      method: "POST",
      headers,
      body: JSON.stringify({
        crm_id: activeCrmId,
        item,
        evento,
        registro_id: recordId,
        dados,
        dados_anteriores: dadosAnteriores || null,
        origem: "usuario",
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const txt = await res.text();
          console.warn(
            `[Automations] ⚠️ Resposta do motor (${res.status}):`,
            txt,
          );
        } else {
          /* automation sent */
        }
      })
      .catch((err) => {
        console.warn(
          "[Automations] Falha ao conectar ao motor de automação:",
          err,
        );
      });
  } catch (err) {
    console.warn("[Automations] Erro ao preparar trigger de automação:", err);
  }
};

const getDataProviderWithCustomMethods = () => {
  const baseDataProvider = getBaseDataProvider();

  return {
    ...baseDataProvider,
    async getList(resource: string, params: GetListParams) {
      const activeCrmId = getTenantActiveCrmId();
      if (TENANT_RESOURCES.includes(resource) && activeCrmId) {
        params = {
          ...params,
          filter: {
            "crm_id@eq": activeCrmId,
            ...params.filter,
          },
        };
      }

      if (resource === "companies") {
        return baseDataProvider.getList("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getList("contacts_summary", params);
      }
      if (resource === "activity_log") {
        const { data, total } = await baseDataProvider.getList(
          "activity_log",
          params,
        );
        // Rename snake_case view columns to camelCase to match Activity type
        return {
          data: data.map((row: any) => ({
            ...row,
            contactNote: row.contact_note ?? undefined,
            dealNote: row.deal_note ?? undefined,
            contact_note: undefined,
            deal_note: undefined,
          })),
          total,
        };
      }
      if (resource === "sales") {
        const activeCrmId = getTenantActiveCrmId();
        let memberSalesIds: any[] = [];

        if (activeCrmId) {
          try {
            const client = getSupabaseClient();
            const { data: members, error } = await client
              .from("crm_members")
              .select("sales_id, user_id")
              .eq("crm_id", activeCrmId);

            if (!error && members && members.length > 0) {
              memberSalesIds = members
                .map((m: any) => m.sales_id)
                .filter(Boolean);
            }
          } catch (e) {
            console.warn("Aviso ao filtrar sales por crm_members:", e);
          }
        }

        if (memberSalesIds.length > 0) {
          params = {
            ...params,
            filter: {
              "id@in": `(${memberSalesIds.join(",")})`,
              ...params.filter,
            },
          };
        } else if (activeCrmId) {
          params = {
            ...params,
            filter: {
              "id@eq": -1,
              ...params.filter,
            },
          };
        }

        const res = await baseDataProvider.getList(resource, params);
        const filteredSales = (res.data || []).filter(
          (u: any) => !isSuperAdminEmail(u.email),
        );
        return {
          data: filteredSales,
          total: filteredSales.length,
        };
      }

      return baseDataProvider.getList(resource, params);
    },
    async delete(resource: string, params: any) {
      if (resource === "sales") {
        return (this as any).salesDelete(params.id);
      }
      const res = await baseDataProvider.delete(resource, params);
      if (params.id) {
        triggerAutomation(
          resource,
          "excluido",
          params.id,
          params.previousData || { id: params.id },
        );
      }
      return res;
    },
    async deleteMany(resource: string, params: any) {
      if (resource === "sales") {
        for (const id of params.ids) {
          await (this as any).salesDelete(id);
        }
        return { data: params.ids };
      }
      const res = await baseDataProvider.deleteMany(resource, params);
      if (Array.isArray(params.ids)) {
        for (const id of params.ids) {
          triggerAutomation(resource, "excluido", id, { id });
        }
      }
      return res;
    },
    async create(resource: string, params: any) {
      const activeCrmId = getTenantActiveCrmId();
      const data = { ...params.data };

      if (TENANT_RESOURCES.includes(resource) && activeCrmId && !data.crm_id) {
        data.crm_id = activeCrmId;
      }

      // Quando o lead/negócio ou contato for criado e não tiver proprietário definido,
      // ele automaticamente será assumido pelo criador do lead (usuário logado)
      if (
        (resource === "deals" || resource === "contacts") &&
        (!data.sales_id || data.sales_id === "")
      ) {
        try {
          const currentSale = await getSale();
          if (currentSale?.id) {
            data.sales_id = currentSale.id;
          }
        } catch (err) {
          console.warn("Aviso ao definir proprietário padrão do lead:", err);
        }
      }

      params = {
        ...params,
        data,
      };
      const res = await baseDataProvider.create(resource, params);
      if (res?.data?.id) {
        triggerAutomation(resource, "criado", res.data.id, res.data);
      }
      return res;
    },
    async update(resource: string, params: any) {
      if (resource === "configuration") {
        const activeCrmId = getTenantActiveCrmId();
        const config = params.data.config || params.data;
        if (activeCrmId) {
          try {
            const client = getSupabaseClient();
            await client
              .from("crms")
              .update({ settings: config })
              .eq("id", activeCrmId);
          } catch (e) {
            console.warn("Could not save to crms table:", e);
          }
        }
        return { data: { id: 1, ...params.data } as any };
      }

      const activeCrmId = getTenantActiveCrmId();
      if (
        TENANT_RESOURCES.includes(resource) &&
        activeCrmId &&
        !params.data?.crm_id
      ) {
        params = {
          ...params,
          data: {
            ...params.data,
            crm_id: activeCrmId,
          },
        };
      }

      // Garante o snapshot anterior (previousData) para operadores como 'mudou_para' e 'valor_anterior_era'
      let snapshotAnterior = params.previousData;
      if (
        !snapshotAnterior &&
        params.id &&
        ["deals", "contacts", "companies", "tasks"].includes(resource)
      ) {
        try {
          const { data: beforeRow } = await getSupabaseClient()
            .from(resource)
            .select("*")
            .eq("id", params.id)
            .maybeSingle();
          if (beforeRow) {
            snapshotAnterior = beforeRow;
          }
        } catch {
          // prossegue sem bloquear o update
        }
      }

      const res = await baseDataProvider.update(resource, params);

      // Dispara automação para alterações em campos (ignora se a alteração for apenas reordenação de índice)
      const targetId = params.id ?? res?.data?.id;
      const dataKeys = Object.keys(params.data || {}).filter(
        (k) => k !== "index" && k !== "crm_id",
      );
      const hasMeaningfulChanges = dataKeys.length > 0;

      if (targetId && hasMeaningfulChanges) {
        triggerAutomation(
          resource,
          "atualizado",
          targetId,
          {
            id: targetId,
            ...(snapshotAnterior || {}),
            ...(params.data || {}),
            ...(res?.data || {}),
          },
          snapshotAnterior,
        );
      }
      return res;
    },
    async getOne(resource: string, params: any) {
      if (resource === "companies") {
        return baseDataProvider.getOne("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getOne("contacts_summary", params);
      }
      if (resource === "configuration") {
        try {
          const activeCrmId = getTenantActiveCrmId();
          if (activeCrmId) {
            const client = getSupabaseClient();
            const { data: crmData } = await client
              .from("crms")
              .select("settings")
              .eq("id", activeCrmId)
              .maybeSingle();

            if (crmData?.settings && Object.keys(crmData.settings).length > 0) {
              return {
                data: {
                  id: 1,
                  config: crmData.settings,
                } as any,
              };
            }
          }
          return {
            data: {
              id: 1,
              config: {},
            } as any,
          };
        } catch {
          return {
            data: {
              id: 1,
              config: {},
            } as any,
          };
        }
      }

      return baseDataProvider.getOne(resource, params);
    },

    async signUp({ email, password, first_name, last_name }: SignUpData) {
      const response = await getSupabaseClient().auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name,
            last_name,
          },
        },
      });

      if (!response.data?.user || response.error) {
        console.error("signUp.error", response.error);
        throw new Error(response?.error?.message || "Failed to create account");
      }

      // Update the is initialized cache
      (getIsInitialized as any)._is_initialized_cache = true;

      return {
        id: response.data.user.id,
        email,
        password,
      };
    },
    async salesCreate(body: SalesFormData) {
      const generatedPassword =
        body.password || `Dotus@${Math.random().toString(36).slice(2, 8)}!`;
      const enrichedBody = { ...body, password: generatedPassword };

      // 1. Tenta criar através da Edge Function 'users' (caso esteja implantada no Supabase)
      try {
        const { data, error } = await getSupabaseClient().functions.invoke<{
          data: Sale;
        }>("users", {
          method: "POST",
          body: enrichedBody,
        });

        if (data?.data && !error) {
          return data.data;
        }

        const errorDetails = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();

        console.warn(
          "Edge Function 'users' não pôde ser executada, iniciando criação direta via Supabase Auth:",
          errorDetails?.message || error?.message,
        );
      } catch (invokeErr) {
        console.warn(
          "Edge Function 'users' inacessível, iniciando criação direta via Supabase Auth:",
          invokeErr,
        );
      }

      // 2. FALLBACK DIRETO ROBUSTO:
      // Cria o usuário via Supabase Auth usando cliente isolado sem persistência de sessão (NUNCA desloga o admin)
      const supabaseUrl = getSupabaseUrl();
      const supabaseAnonKey = "dotus_crm_client_public";

      const authClient = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
        },
      });

      const { data: signUpData, error: signUpError } =
        await authClient.auth.signUp({
          email: enrichedBody.email,
          password: generatedPassword,
          options: {
            data: {
              first_name: enrichedBody.first_name,
              last_name: enrichedBody.last_name,
            },
          },
        });

      if (
        signUpError &&
        signUpError.message &&
        !signUpError.message.toLowerCase().includes("already")
      ) {
        throw new Error(signUpError.message);
      }

      const client = getSupabaseClient();
      const userId = signUpData?.user?.id;

      try {
        await client.rpc("set_team_user_password", {
          user_email: enrichedBody.email,
          new_password: generatedPassword,
        });
      } catch (rpcErr) {
        console.warn("RPC set_team_user_password opcional:", rpcErr);
      }

      // Aguarda 600ms para o trigger do banco inserir a linha em public.sales
      await new Promise((r) => setTimeout(r, 600));

      // Busca na tabela public.sales pelo e-mail
      let { data: existingSale } = await client
        .from("sales")
        .select("*")
        .eq("email", enrichedBody.email)
        .maybeSingle();

      if (!existingSale && userId) {
        // Se o trigger não tiver inserido, insere diretamente
        const { data: insertedSale, error: insertErr } = await client
          .from("sales")
          .insert({
            user_id: userId,
            email: enrichedBody.email,
            first_name: enrichedBody.first_name,
            last_name: enrichedBody.last_name,
            administrator: Boolean(enrichedBody.administrator),
            disabled: Boolean(enrichedBody.disabled),
            dotus_instance_id:
              enrichedBody.dotus_instance_id?.trim() ||
              enrichedBody.first_name?.trim() ||
              null,
          })
          .select("*")
          .maybeSingle();

        if (insertErr) {
          console.warn("Aviso ao inserir em public.sales:", insertErr);
        }
        existingSale = insertedSale;
      }

      if (existingSale) {
        // Atualiza permissões e status
        const updatePayload: any = {
          first_name: enrichedBody.first_name,
          last_name: enrichedBody.last_name,
          administrator: Boolean(enrichedBody.administrator),
          disabled: Boolean(enrichedBody.disabled),
        };
        if (enrichedBody.status) updatePayload.status = enrichedBody.status;
        if (enrichedBody.deal_permission)
          updatePayload.deal_permission = enrichedBody.deal_permission;
        if (enrichedBody.global_permission)
          updatePayload.global_permission = enrichedBody.global_permission;
        const resolvedInstanceId =
          enrichedBody.dotus_instance_id?.trim() ||
          enrichedBody.first_name?.trim() ||
          null;
        updatePayload.dotus_instance_id = resolvedInstanceId;

        try {
          const { error: rpcErr } = await client.rpc("update_sale_member", {
            p_sales_id: Number(existingSale.id),
            p_first_name: enrichedBody.first_name,
            p_last_name: enrichedBody.last_name,
            p_administrator: Boolean(enrichedBody.administrator),
            p_disabled: Boolean(enrichedBody.disabled),
            p_status: enrichedBody.status || null,
            p_deal_permission: enrichedBody.deal_permission || null,
            p_global_permission: enrichedBody.global_permission || null,
          });

          if (!rpcErr) {
            const { data: updated } = await client
              .from("sales")
              .select("*")
              .eq("id", existingSale.id)
              .maybeSingle();
            if (updated) existingSale = updated;
          } else {
            const { data: updated } = await client
              .from("sales")
              .update(updatePayload)
              .eq("id", existingSale.id)
              .select("*")
              .maybeSingle();
            if (updated) existingSale = updated;
          }
        } catch {}

        // Associa o novo membro ao CRM ativo atual
        const activeCrmId = getTenantActiveCrmId();
        const targetUserId = userId || existingSale.user_id;
        if (activeCrmId && targetUserId) {
          try {
            await client.from("crm_members").upsert(
              {
                crm_id: activeCrmId,
                user_id: targetUserId,
                sales_id: existingSale.id,
                role: enrichedBody.administrator ? "admin" : "member",
              },
              { onConflict: "crm_id,user_id" },
            );
          } catch (crmErr) {
            console.warn("Aviso ao vincular membro ao CRM:", crmErr);
          }
        }

        return existingSale as Sale;
      }

      // Se por algum motivo o trigger e a tabela demorarem, retorna o objeto estruturado
      return {
        id: userId || Date.now(),
        email: enrichedBody.email,
        first_name: enrichedBody.first_name,
        last_name: enrichedBody.last_name,
        administrator: Boolean(enrichedBody.administrator),
        disabled: Boolean(enrichedBody.disabled),
        status: enrichedBody.status || "invited",
        deal_permission: enrichedBody.deal_permission || "user",
        global_permission: enrichedBody.global_permission || "user",
      } as unknown as Sale;
    },
    async salesUpdate(
      id: Identifier,
      data: Partial<Omit<SalesFormData, "password">>,
    ) {
      const { email, first_name, last_name, administrator, avatar, disabled } =
        data;

      const { data: updatedData, error } =
        await getSupabaseClient().functions.invoke<{
          data: Sale;
        }>("users", {
          method: "PATCH",
          body: {
            sales_id: id,
            email,
            first_name,
            last_name,
            administrator,
            disabled,
            avatar,
          },
        });

      if (!updatedData || error) {
        console.warn(
          "Aviso: Edge Function users indisponível para PATCH. Atualizando via RPC update_sale_member:",
          error,
        );
        const client = getSupabaseClient();

        let directUpdated: any = null;
        try {
          const { error: rpcErr } = await client.rpc("update_sale_member", {
            p_sales_id: Number(id),
            p_first_name: first_name ?? null,
            p_last_name: last_name ?? null,
            p_email: email ?? null,
            p_avatar: avatar ?? null,
            p_administrator:
              administrator !== undefined ? Boolean(administrator) : null,
            p_disabled: disabled !== undefined ? Boolean(disabled) : null,
            p_status: (data as any).status ?? null,
            p_deal_permission: (data as any).deal_permission ?? null,
            p_global_permission: (data as any).global_permission ?? null,
            p_account_configured:
              (data as any).account_configured !== undefined
                ? Boolean((data as any).account_configured)
                : null,
            p_is_superadmin:
              (data as any).is_superadmin !== undefined
                ? Boolean((data as any).is_superadmin)
                : null,
          });

          if (!rpcErr) {
            const { data: refetched } = await client
              .from("sales")
              .select("*")
              .eq("id", id)
              .maybeSingle();
            directUpdated = refetched;
            if ((data as any).dotus_instance_id !== undefined) {
              try {
                await client
                  .from("sales")
                  .update({
                    dotus_instance_id: (data as any).dotus_instance_id || null,
                  })
                  .eq("id", id);
                if (directUpdated) {
                  directUpdated.dotus_instance_id =
                    (data as any).dotus_instance_id || null;
                }
              } catch {}
            }
          } else {
            console.warn(
              "RPC update_sale_member retornou erro, tentando update direto:",
              rpcErr,
            );
          }
        } catch (callErr) {
          console.warn("Falha ao invocar RPC update_sale_member:", callErr);
        }

        if (!directUpdated) {
          const updatePayload: any = {
            first_name,
            last_name,
            email,
            administrator,
            disabled,
            avatar,
          };
          if ((data as any).status) updatePayload.status = (data as any).status;
          if ((data as any).deal_permission)
            updatePayload.deal_permission = (data as any).deal_permission;
          if ((data as any).global_permission)
            updatePayload.global_permission = (data as any).global_permission;
          if ((data as any).password)
            updatePayload.password = (data as any).password;
          if ((data as any).account_configured !== undefined)
            updatePayload.account_configured = (data as any).account_configured;
          if ((data as any).dotus_instance_id !== undefined)
            updatePayload.dotus_instance_id =
              (data as any).dotus_instance_id || null;

          const { data: fallbackUpdated, error: directErr } = await client
            .from("sales")
            .update(updatePayload)
            .eq("id", id)
            .select("*")
            .maybeSingle();

          if (directErr) {
            console.error("salesUpdate direct error:", directErr);
            throw new Error("Falha ao atualizar usuário: " + directErr.message);
          }
          directUpdated = fallbackUpdated;
        }

        return (directUpdated || { id, ...data }) as Sale;
      }

      return updatedData.data;
    },
    async salesDelete(salesId: Identifier) {
      const activeCrmId = getTenantActiveCrmId();
      const client = getSupabaseClient();

      // 1. Remove o usuário da equipe deste CRM em crm_members
      if (activeCrmId) {
        const { error: memberErr } = await client
          .from("crm_members")
          .delete()
          .eq("crm_id", activeCrmId)
          .eq("sales_id", salesId);

        if (memberErr) {
          console.warn("Aviso ao remover de crm_members:", memberErr);
        }
      }

      // 2. Se o usuário não pertencer a nenhum outro CRM, tenta deletar da tabela sales (ou marca disabled)
      try {
        const { data: otherMemberships } = await client
          .from("crm_members")
          .select("id")
          .eq("sales_id", salesId);

        if (!otherMemberships || otherMemberships.length === 0) {
          const { error: delErr } = await client
            .from("sales")
            .delete()
            .eq("id", salesId);

          if (delErr) {
            const { error: rpcDisableErr } = await client.rpc(
              "update_sale_member",
              {
                p_sales_id: Number(salesId),
                p_disabled: true,
                p_status: "disabled",
              },
            );
            if (rpcDisableErr) {
              await client
                .from("sales")
                .update({ disabled: true, status: "disabled" })
                .eq("id", salesId);
            }
          }
        }
      } catch (cleanupErr) {
        console.warn("Aviso na limpeza do usuário em sales:", cleanupErr);
      }

      return { data: { id: salesId } };
    },
    async updatePassword(id: Identifier) {
      const { data: passwordUpdated, error } =
        await getSupabaseClient().functions.invoke<boolean>("update_password", {
          method: "PATCH",
          body: {
            sales_id: id,
          },
        });

      if (!passwordUpdated || error) {
        console.error("update_password.error", error);
        throw new Error("Failed to update password");
      }

      return passwordUpdated;
    },
    async unarchiveDeal(deal: Deal) {
      // get all deals where stage is the same as the deal to unarchive
      const { data: deals } = await baseDataProvider.getList<Deal>("deals", {
        filter: { stage: deal.stage },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "index", order: "ASC" },
      });

      // set index for each deal starting from 1, if the deal to unarchive is found, set its index to the last one
      const updatedDeals = deals.map((d, index) => ({
        ...d,
        index: d.id === deal.id ? 0 : index + 1,
        archived_at: d.id === deal.id ? null : d.archived_at,
      }));

      return await Promise.all(
        updatedDeals.map((updatedDeal) =>
          baseDataProvider.update("deals", {
            id: updatedDeal.id,
            data: updatedDeal,
            previousData: deals.find((d) => d.id === updatedDeal.id),
          }),
        ),
      );
    },
    async isInitialized() {
      return getIsInitialized();
    },
    async mergeContacts(sourceId: Identifier, targetId: Identifier) {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "merge_contacts",
        {
          method: "POST",
          body: { loserId: sourceId, winnerId: targetId },
        },
      );

      if (error) {
        console.error("merge_contacts.error", error);
        throw new Error("Failed to merge contacts");
      }

      return data;
    },
    async getConfiguration(): Promise<ConfigurationContextValue> {
      try {
        const activeCrmId = getTenantActiveCrmId();
        if (activeCrmId) {
          const client = getSupabaseClient();
          const { data: crmData } = await client
            .from("crms")
            .select("settings")
            .eq("id", activeCrmId)
            .maybeSingle();

          if (crmData?.settings && Object.keys(crmData.settings).length > 0) {
            return crmData.settings as ConfigurationContextValue;
          }
        }
        return {} as ConfigurationContextValue;
      } catch {
        return {} as ConfigurationContextValue;
      }
    },
    async updateConfiguration(
      config: ConfigurationContextValue,
    ): Promise<ConfigurationContextValue> {
      try {
        const activeCrmId = getTenantActiveCrmId();
        if (activeCrmId) {
          const client = getSupabaseClient();
          await client
            .from("crms")
            .update({ settings: config })
            .eq("id", activeCrmId);
        }
        return config;
      } catch {
        return config;
      }
    },
  } satisfies DataProvider;
};

export type CrmDataProvider = ReturnType<
  typeof getDataProviderWithCustomMethods
>;

const processConfigLogo = async (logo: any): Promise<string> => {
  if (typeof logo === "string") return logo;
  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
    return logo.src;
  }
  return logo?.src ?? "";
};

const lifeCycleCallbacks: ResourceCallbacks[] = [
  {
    resource: "configuration",
    beforeUpdate: async (params) => {
      const config = params.data.config;
      if (config) {
        config.lightModeLogo = await processConfigLogo(config.lightModeLogo);
        config.darkModeLogo = await processConfigLogo(config.darkModeLogo);
      }
      return params;
    },
  },
  {
    resource: "contact_notes",
    beforeSave: async (data: ContactNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "deal_notes",
    beforeSave: async (data: DealNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "sales",
    beforeSave: async (data: Sale, _, __) => {
      if (data.avatar) {
        await uploadToBucket(data.avatar);
      }
      return data;
    },
  },
  {
    resource: "contacts",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "first_name",
        "last_name",
        "company_name",
        "title",
        "email",
        "phone",
        "background",
      ])(params);
    },
  },
  {
    resource: "companies",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "name",
        "phone_number",
        "website",
        "zipcode",
        "city",
        "state_abbr",
      ])(params);
    },
    beforeCreate: async (params) => {
      const createParams = await processCompanyLogo(params);

      return {
        ...createParams,
        data: {
          created_at: new Date().toISOString(),
          ...createParams.data,
        },
      };
    },
    beforeUpdate: async (params) => {
      return await processCompanyLogo(params);
    },
  },
  {
    resource: "contacts_summary",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["first_name", "last_name"])(params);
    },
  },
  {
    resource: "deals",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "category", "description"])(params);
    },
  },
];

export const getDataProvider = () => {
  return withLifecycleCallbacks(
    getDataProviderWithCustomMethods(),
    lifeCycleCallbacks,
  ) as CrmDataProvider;
};

const applyFullTextSearch = (columns: string[]) => (params: GetListParams) => {
  if (!params.filter?.q) {
    return params;
  }
  const { q, ...filter } = params.filter;
  return {
    ...params,
    filter: {
      ...filter,
      "@or": columns.reduce((acc, column) => {
        if (column === "email")
          return {
            ...acc,
            [`email_fts@ilike`]: q,
          };
        if (column === "phone")
          return {
            ...acc,
            [`phone_fts@ilike`]: q,
          };
        else
          return {
            ...acc,
            [`${column}@ilike`]: q,
          };
      }, {}),
    },
  };
};

const uploadToBucket = async (fi: RAFile) => {
  if (!fi.src.startsWith("blob:") && !fi.src.startsWith("data:")) {
    // Sign URL check if path exists in the bucket
    if (fi.path) {
      const { error } = await getSupabaseClient()
        .storage.from(ATTACHMENTS_BUCKET)
        .createSignedUrl(fi.path, 60);

      if (!error) {
        return fi;
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src)
        .then((res) => {
          if (res.status !== 200) {
            return null;
          }
          return res.blob();
        })
        .catch(() => null)
    : fi.rawFile;

  if (dataContent == null) {
    // We weren't able to download the file from its src (e.g. user must be signed in on another website to access it)
    // or the file has no content (not probable)
    // In that case, just return it as is: when trying to download it, users should be redirected to the other website
    // and see they need to be signed in. It will then be their responsibility to upload the file back to the note.
    return fi;
  }

  const file = fi.rawFile;
  const fileParts = file.name.split(".");
  const fileExt = fileParts.length > 1 ? `.${file.name.split(".").pop()}` : "";
  const fileName = `${Math.random()}${fileExt}`;
  const filePath = `${fileName}`;
  const { error: uploadError } = await getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .upload(filePath, dataContent);

  if (uploadError) {
    console.error("uploadError", uploadError);
    throw new Error("Failed to upload attachment");
  }

  const { data } = getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .getPublicUrl(filePath);

  fi.path = filePath;
  fi.src = data.publicUrl;

  // save MIME type
  const mimeType = file.type;
  fi.type = mimeType;

  return fi;
};
